import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from './organization';
import { reportRangeBounds } from '../utils/reportDates';
import ErrorHandler from '../utils/ErrorHandler';
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { supabase, queryClient } from './client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveCalendar } from './calendars';
import { insertActivity } from './activity';
import { isBibleStudiesEnabled } from './bibleStudies';
import { buildUpdateDetails, joinDetails } from '../utils/buildActivityDetails';

const getSmallGroupTypes = async () => {
    const { error, data } = await supabase.from('SmallGroupTypes').select('*, Role:Roles(id, name), TypeCustomFields:SmallGroupTypeCustomFields(CustomField:CustomFields(id, name, type, options, position, required, sectionID))');

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroupTypes", error}, `Something went wrong fetching the small group types`)
        return [];
    }

    data.forEach(type => {
        const displayMap = type.customFieldDisplay || {};
        type.CustomFields = (type.TypeCustomFields || []).map(r => {
            const field = r.CustomField;
            if(field) field.displayOn = displayMap[field.id] || 'group';
            return field;
        }).filter(Boolean);
        delete type.TypeCustomFields;
    });

    return data;
}


const saveSmallGroupType = async (smallGroupTypeDetails) => {
    const originalValues = smallGroupTypeDetails.originalValues || null;
    delete smallGroupTypeDetails.originalValues;

    const customFields = smallGroupTypeDetails.CustomFields !== undefined ? smallGroupTypeDetails.CustomFields : null;
    delete smallGroupTypeDetails.CustomFields;

    if(customFields !== null){
        const rawDisplayMap = smallGroupTypeDetails.customFieldDisplay || {};
        const customFieldDisplay = {};
        customFields.forEach(f => { customFieldDisplay[f.id] = rawDisplayMap[f.id] || 'group'; });
        smallGroupTypeDetails.customFieldDisplay = customFieldDisplay;
    }

    if(!smallGroupTypeDetails.id){
        const orgID = await getCurrentOrgID();

        // Remove relation field — not a column on SmallGroupTypes
        delete smallGroupTypeDetails.Role;

        const calendar = await saveCalendar({
            id: smallGroupTypeDetails.id,
            name: smallGroupTypeDetails.name + ' (Small Group)',
            description: `A calendar to show meetings and events for ${smallGroupTypeDetails.name} small groups.`,
            color: '#'+Math.floor(Math.random()*16777215).toString(16),
            organizationID: orgID,
        });

        if(calendar?.id){
            smallGroupTypeDetails.calendarID = calendar.id;
            smallGroupTypeDetails.organizationID = orgID;

            const { error, data } = await supabase.from('SmallGroupTypes').insert(smallGroupTypeDetails).select().maybeSingle();

            if(error){
                ErrorHandler(Error(error.message), {mutation: "createSmallGroupType", input: smallGroupTypeDetails, ...error}, `Something went wrong creating that small group type`)
                throw Error()
            }

            if(customFields?.length > 0 && data?.id) {
                await supabase.from('SmallGroupTypeCustomFields').insert(
                    customFields.map((f, i) => ({ smallGroupTypeID: data.id, customFieldID: f.id, position: i }))
                );
            }

            insertActivity({ subjectType: 'SmallGroupType', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

            return data;
        }
        else{
            ErrorHandler(Error(), {mutation: "createSmallGroupType", input: smallGroupTypeDetails}, `Something went wrong creating that small group type`)
            throw Error()
        }
    }
    else{
        const cleanDetails = { ...smallGroupTypeDetails };
        delete cleanDetails.Role;
        const { error, data } = await supabase.from('SmallGroupTypes').update(cleanDetails).eq('id', cleanDetails.id).select().maybeSingle();

        if(error){
            ErrorHandler(Error(error.message), {mutation: "updateSmallGroupType", input: smallGroupTypeDetails, ...error}, `Something went wrong updating that small group type`)
            throw Error()
        }

        if(customFields !== null) {
            await supabase.from('SmallGroupTypeCustomFields').delete().eq('smallGroupTypeID', cleanDetails.id);
            if(customFields.length > 0) {
                await supabase.from('SmallGroupTypeCustomFields').insert(
                    customFields.map((f, i) => ({ smallGroupTypeID: cleanDetails.id, customFieldID: f.id, position: i }))
                );
            }
        }

        const details = originalValues ? buildUpdateDetails(originalValues, cleanDetails, { name: { label: 'Name' }, description: { label: 'Description' } }) : null;
        insertActivity({ subjectType: 'SmallGroupType', subjectID: data.id, subjectName: data.name, verb: 'update', details }).catch(() => {});

        return data;
    }
}

const getSmallGroupType = async (id) => {
    const { error, data } = await supabase.from('SmallGroupTypes').select('*, Role:Roles(id, name), TypeCustomFields:SmallGroupTypeCustomFields(CustomField:CustomFields(id, name, type, options, position, required, sectionID))').eq('id', id).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroupType", error, id}, `Something went wrong fetching the small group type`)
        return null;
    }

    if(data) {
        const displayMap = data.customFieldDisplay || {};
        data.CustomFields = (data.TypeCustomFields || []).map(r => {
            const field = r.CustomField;
            if(field) field.displayOn = displayMap[field.id] || 'group';
            return field;
        }).filter(Boolean);
        delete data.TypeCustomFields;
    }

    return data;
}

const deleteSmallGroupType = async (id) => {
    const { data: existing } = await supabase.from('SmallGroupTypes').select('name').eq('id', id).maybeSingle();

    const { error } = await supabase.from('SmallGroupTypes').delete().eq('id', id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteSmallGroupType", error, id}, `Something went wrong deleting the small group type`)
        throw Error
    }

    insertActivity({ subjectType: 'SmallGroupType', subjectID: id, subjectName: existing?.name, verb: 'delete' }).catch(() => {});
}

const getSmallGroups = async ({pageParam = 0, searchTerm, filters = null, sortKey = null, sortDirection = null}) => {
    const orgID = await getCurrentOrgID();
    const nextPage = pageParam + 1
    const filterValues = filters?.reduce((acc, filter) => ({...acc, [filter.id]: filter.value}), {})

    // Flag-on orgs exclude Bible Study types from ordinary Small Groups lists
    // ('SMALL_GROUP' purpose); flag-off keeps the legacy unfiltered contract.
    const bibleStudiesEnabled = await isBibleStudiesEnabled();

    const { error, data } = await supabase.rpc('get_small_groups', {
        p_org_id: orgID,
        p_leader_id: filterValues?.leader?.id || null,
        p_small_group_type_id: filterValues?.smallGroupType?.id || null,
        p_status: filterValues?.status || null,
        p_search_term: searchTerm || null,
        p_offset: pageParam * 25,
        p_limit: 25,
        p_sort_key: sortKey || null,
        p_sort_direction: sortDirection || null,
        p_purpose: bibleStudiesEnabled ? 'SMALL_GROUP' : null
    });

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroups", input: {organizationID: orgID, filters, searchTerm}}, `Something went wrong fetching the Small Groups`)
        return {nextPage: undefined, data: []};
    }
    
    return {nextPage: data.length < 25 ? undefined : nextPage, data}

}

// purpose: explicit SmallGroupTypes.purpose scope. 'BIBLE_STUDY' → only
// studies (Bible Studies pickers); 'SMALL_GROUP' → only ordinary groups;
// null → flag decides ('SMALL_GROUP' when enableBibleStudies is on so
// studies leave generic pickers, legacy unfiltered when off).
const getSmallGroupsForDropdown = async (smallGroupTypeID = null, purpose = null) => {
    const orgID = await getCurrentOrgID();
    const loggedInUser = queryClient.getQueryData(QueryKeys.people.loggedInUser());

    const resolvedPurpose = purpose ?? (await isBibleStudiesEnabled() ? 'SMALL_GROUP' : null);
    const purposeScoped = resolvedPurpose === 'BIBLE_STUDY' || resolvedPurpose === 'SMALL_GROUP';

    let query = supabase.from('SmallGroups').select(`
        id, name, status, smallGroupTypeID,
        ${purposeScoped ? 'SmallGroupType:SmallGroupTypes!inner(purpose),' : ''}
        Leader:People!SmallGroups_leaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        CoLeader:People!SmallGroups_coLeaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))
    `)
    .eq('organizationID', orgID)
    .not('status', 'in', '("COMPLETED","CANCELED")')

    if (purposeScoped) {
        query = resolvedPurpose === 'BIBLE_STUDY'
            ? query.eq('SmallGroupType.purpose', 'BIBLE_STUDY')
            : query.neq('SmallGroupType.purpose', 'BIBLE_STUDY');
    }

    if (smallGroupTypeID) {
        query = query.eq('smallGroupTypeID', smallGroupTypeID);
    }

    const { error, data } = await query;

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroupsForDropdown", input: {organizationID: orgID, smallGroupTypeID}}, `Something went wrong fetching the Small Groups`)
        return [];
    }

    const userPersonID = loggedInUser?.id;

    return data.sort((a, b) => {
        const aIsUserActive = a.status === 'ACTIVE' && (a.Leader?.id === userPersonID || a.CoLeader?.id === userPersonID || a.Students?.some(s => s.Person?.id === userPersonID));
        const bIsUserActive = b.status === 'ACTIVE' && (b.Leader?.id === userPersonID || b.CoLeader?.id === userPersonID || b.Students?.some(s => s.Person?.id === userPersonID));

        if(aIsUserActive && !bIsUserActive) return -1;
        if(!aIsUserActive && bIsUserActive) return 1;

        const aIsActive = a.status === 'ACTIVE';
        const bIsActive = b.status === 'ACTIVE';

        if(aIsActive && !bIsActive) return -1;
        if(!aIsActive && bIsActive) return 1;

        const aLeader = a.Leader?.firstName?.toLowerCase() || '';
        const bLeader = b.Leader?.firstName?.toLowerCase() || '';
        return aLeader.localeCompare(bLeader);
    });
}

const getSmallGroup = async (id) => {
    const orgID = await getCurrentOrgID();

    if(!id || !orgID){
        return [];
    }

    const { error, data } = await supabase.from('SmallGroups').select(`*, 
        Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), 
        Event:Events(EventInstances(*, Students:Attendance(*, Person:People!attendeeID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))), 
        Leader:People!SmallGroups_leaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey, SmallGroups!SmallGroups_leaderID_fkey(id)),
        CoLeader:People!SmallGroups_coLeaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        SmallGroupType:SmallGroupTypes(id, name, icon, purpose, customFieldDisplay, TypeCustomFields:SmallGroupTypeCustomFields(CustomField:CustomFields(id, name, type, options, position, required, sectionID)))`)
    .eq('organizationID', orgID)
    .eq('id', id)
    .order('startTime', {referencedTable: 'Event.EventInstances', ascending: false})
    // .order('firstName', {referencedTable: 'Students', ascending: false})
    // .order('Students.firstName', {referencedTable: 'Sessions', ascending: false})
    .maybeSingle()

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroups", input: {organizationID: orgID, id}}, `Something went wrong fetching the Small Groups`)
        return [];
    }

    if(data?.SmallGroupType) {
        const displayMap = data.SmallGroupType.customFieldDisplay || {};
        data.SmallGroupType.CustomFields = (data.SmallGroupType.TypeCustomFields || []).map(r => {
            const field = r.CustomField;
            if(field) field.displayOn = displayMap[field.id] || 'group';
            return field;
        }).filter(Boolean);
        delete data.SmallGroupType.TypeCustomFields;
    }

    return data
}


const getSmallGroupSession = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('EventInstances').select(`*, 
        Students:Attendance(*, Person:People!fk_Attendance_Person(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), 
        EventSpeakers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        Event:Events(*, SmallGroup:SmallGroups(id, name, Leader:People!SmallGroups_leaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))))`) 
    .eq('organizationID', orgID)
    .eq('id', id)
    .order('firstName', {referencedTable: 'Students.Person', ascending: false})
    .maybeSingle()

    if(error){
        ErrorHandler(Error(error.message), {query: "getSmallGroupSession", input: {organizationID: orgID, id}}, `Something went wrong fetching the Small Group Session`)
        return [];
    }

    return data
}


const saveSmallGroup = async (details) => {
    const orgID = await getCurrentOrgID();
    const originalValues = details.originalValues || null;

    if(details.lessonTime){
        const date = new Date(details.lessonTime);
        details.lessonTime = date?.toTimeString().substring(0,8);
    }

    const newSmallGroup = {
        id: details.id,
        smallGroupTypeID: details.smallGroupTypeID,
        organizationID: orgID,
        status: details.status,
        leaderID: details.leaderID,
        coLeaderID: details.coLeaderID || null,
        name: details.name,
        description: details.description || null,
        customFieldValues: details.customFieldValues || {},
        notifyLeaderOnMemberAdd: details.notifyLeaderOnMemberAdd,
    }

    if(!details.id){
        return createSmallGroup(newSmallGroup, details.Students);
    }
    else{
        return updateSmallGroup(newSmallGroup, details.Students, originalValues);
    }
}


const createSmallGroup = async (newSmallGroup, students) => {
    //Assign the Small Group group type if one isn't set
    if(!newSmallGroup.smallGroupTypeID){
        const orgID = await getCurrentOrgID();
        const {data, error} = await supabase.from('SmallGroupTypes').select('id').eq('organizationID', orgID).order('createdAt', {ascending: true}).limit(1).maybeSingle();

        if(!data || error){
            ErrorHandler(Error(error?.message || "Could not get group type automatically"), {mutation: "createSmallGroup-getType", error, newSmallGroup}, `Could not create Small Group. Failed to retrieve a group type automatically`)
            throw Error
        }
        
        newSmallGroup.smallGroupTypeID = data?.id;
    }

    //Get the calendar ID from the small group type
    const type = await supabase.from('SmallGroupTypes').select('calendarID').eq('id', newSmallGroup.smallGroupTypeID).maybeSingle();

    if(!type?.data?.calendarID || type.error){
        ErrorHandler(Error(type.error?.message || "No calendar found on group type"), {mutation: "createSmallGroup-getType", error: type.error, newSmallGroup}, `Could not create Group. Failed to retrieve a calendar from the Group Type`)
        throw Error
    }

    //Create the Event for the Group
    const { error: errorEvent, data: newEvent } = await supabase.from('Events').insert({
        calendarID: type.data.calendarID,
        name: newSmallGroup.name,
        organizationID: newSmallGroup.organizationID,
    }).select('id').maybeSingle();

    if(errorEvent){
        ErrorHandler(Error(errorEvent.message), {mutation: "createSmallGroup-event", error: errorEvent, newSmallGroup}, `Something went wrong creating the Small Group`)
        throw Error
    }

    //Create the Small Group
    const { error, data } = await supabase.from('SmallGroups').insert({...newSmallGroup, status: newSmallGroup.status || 'ACTIVE', eventID: newEvent.id, lastStatusChange: new Date()}).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createSmallGroup", error, newSmallGroup}, `Something went wrong creating the Small Group`)
        throw Error
    }
    
    //Add the Students to the Small Group
    if(students.length > 0){
        const { error: errorStudents } = await supabase.from('SmallGroupMembers').insert(students?.map(s => ({organizationID: newSmallGroup.organizationID, smallGroupID: data.id, memberID: s.id})));

        if(errorStudents){
            ErrorHandler(Error(errorStudents.message), {mutation: "createSmallGroupMembers", errorStudents, students}, `We created the Small Group, but something went wrong adding the members, check that the members were added correctly.`)
            throw Error
        }
    }

    insertActivity({ subjectType: 'SmallGroup', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

    return {...data, Students: students};
}


const updateSmallGroup = async (smallGroup, students, originalValues = null) => {
    const { error, data } = await supabase.from('SmallGroups').update(smallGroup).eq('id', smallGroup.id).select('*, Students:SmallGroupMembers(memberID)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateSmallGroup", error, smallGroup}, `Something went wrong updating the Small Group`)
        throw Error
    }

    let studentDetails = [];
    if(students){
        const {itemsToAdd, itemsToDelete} = findAssociativeChanges(students.map(s => s.id), data.Students?.map(s => s.memberID));

        if(itemsToAdd && itemsToAdd.length > 0){
            const { error: errorStudents } = await supabase.from('SmallGroupMembers').insert(itemsToAdd?.map(s => ({organizationID: smallGroup.organizationID, smallGroupID: smallGroup.id, memberID: s})));

            if(errorStudents){
                ErrorHandler(errorStudents, {mutation: "updateSmallGroupMembersAdd", errorStudents, students}, `We updated the Small Group, but something went wrong adding new members, check that the members were added correctly.`)
            }
        }

        if(itemsToDelete && itemsToDelete.length > 0){
            const { error: errorStudentsDelete } = await supabase.from('SmallGroupMembers').delete().eq('smallGroupID', smallGroup.id).in('memberID', itemsToDelete);

            if(errorStudentsDelete){
                ErrorHandler(errorStudentsDelete, {mutation: "updateSmallGroupMembersDelete", errorStudentsDelete, students}, `We updated the Small Group, but something went wrong deleting the removed members.`)
            }
        }

        if(itemsToAdd?.length) studentDetails.push(`- Added ${itemsToAdd.length} member(s)`);
        if(itemsToDelete?.length) studentDetails.push(`- Removed ${itemsToDelete.length} member(s)`);
    }

    const scalarDetails = originalValues ? buildUpdateDetails(originalValues, smallGroup, {
        name: { label: 'Name' },
        status: { label: 'Status' },
    }) : null;
    insertActivity({ subjectType: 'SmallGroup', subjectID: smallGroup.id, subjectName: smallGroup.name, verb: 'update', details: joinDetails(scalarDetails, studentDetails) }).catch(() => {});

    return {...smallGroup, Students: students};
}


const deleteSmallGroup = async (smallGroupID) => {
    const { data: existing } = await supabase.from('SmallGroups').select('name').eq('id', smallGroupID).maybeSingle();

    const { error } = await supabase.from('SmallGroups').delete().eq('id', smallGroupID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteSmallGroup", error, smallGroupID}, `Something went wrong deleting the Small Group`)
        throw Error
    }

    Toast.delayShow({type: 'success',text2: 'Successfully deleted Small Group'})

    insertActivity({ subjectType: 'SmallGroup', subjectID: smallGroupID, subjectName: existing?.name, verb: 'delete' }).catch(() => {});
}   


const saveSmallGroupSession = async ({details}) => {
    const orgID = await getCurrentOrgID();

    const newSmallGroupSession = {
        id: details.id,
        organizationID: orgID,
        startTime: details.sessionDate,
        notes: details.notes,
        customFieldValues: details.customFieldValues || {},
    }

    if(!details.id){
        return createSmallGroupSession(newSmallGroupSession, details.smallGroupID, details.Attendees, details.leaderID);
    }
    else{
        return updateSmallGroupSession(newSmallGroupSession, details.Attendees, details.leaderID, details.smallGroupID);
    }
}

const createSmallGroupSession = async (newSmallGroupSession, smallGroupID, attendees, leaderID) => {
    const smallGroup = await supabase.from('SmallGroups').select('eventID, status').eq('id', smallGroupID).maybeSingle();
    if(!smallGroup.data.eventID || smallGroup.error){
        ErrorHandler(Error(smallGroup.error?.message || "Small Group not found"), {mutation: "createSmallGroupSession", smallGroupID, newSmallGroupSession}, `Something went wrong creating the Small Group Session`)
        throw Error
    }

    const { error, data } = await supabase.from('EventInstances').insert({...newSmallGroupSession, eventID: smallGroup.data.eventID}).select('*, Event:Events(SmallGroup:SmallGroups(status, leaderID))').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createSmallGroupSession", error, newSmallGroupSession}, `Something went wrong creating the Small Group Session`)
        throw Error
    }

    //Insert a new EventSpeaker entry for the leaderID
    const eventSpeaker = await supabase.from('EventSpeakers').insert({eventInstanceID: data.id, personID: leaderID, organizationID: newSmallGroupSession.organizationID}).select().maybeSingle();

    if(eventSpeaker.error){
        ErrorHandler(Error(eventSpeaker.error.message), {mutation: "createSmallGroupSession_EventSpeaker", eventSpeaker}, `Something went wrong creating the Small Group Session`)
        throw Error
    }

    //Update this Small Group to be active if it isn't already, since we're reporting a new session
    if(smallGroup.data.status !== 'ACTIVE'){
        const {error:statusUpdateError} = await supabase.from('SmallGroups').update({status: 'ACTIVE', lastStatusChange: new Date()}).eq('id', smallGroupID);

        if(statusUpdateError){
            ErrorHandler(Error(statusUpdateError.message), {mutation: "createSmallGroupSession_UpdateStatus", statusUpdateError})
            throw Error
        }
    }

    if(attendees && attendees.length > 0){
        const {error:attendanceError} = await supabase.from('Attendance').insert(attendees.map(a => ({eventInstanceID: data.id, attendeeID: a.id, organizationID: newSmallGroupSession.organizationID, submitterID: data.Event.SmallGroup?.leaderID})));

        if(attendanceError){
            ErrorHandler(Error(attendanceError.message), {mutation: "createSmallGroupSession_Attendance", attendanceError})
            throw Error
        }
    }

    // if(newSmallGroupSession.SmallGroup?.Leader?.breezePersonID){
    //     sendSmallGroupToWebsite({
    //         "pbs-date": newSmallGroupSession.sessionDate,
    //         "pbs-status": newSmallGroupSession.SmallGroup?.status,
    //         "pbs-notes": newSmallGroupSession.notes,
    //         "pbs-teacher": newSmallGroupSession.SmallGroup?.Leader?.breezePersonID,
    //         "pbs-students[]": newSmallGroupSession.SmallGroup?.Students?.map(s => s.breezePersonID),
    //     })
    // }

    const groupName = smallGroup.data?.name || 'Small Group';
    insertActivity({ subjectType: 'SmallGroupSession', subjectID: smallGroupID, subjectName: groupName, verb: 'create' }).catch(() => {});

    return data;
}


const updateSmallGroupSession = async (smallGroupSession, attendees, leaderID, smallGroupID) => {
    const smallGroup = await supabase.from('SmallGroups').select('eventID, status, name').eq('id', smallGroupID).maybeSingle();

    const { error, data } = await supabase.from('EventInstances').update({...smallGroupSession, eventID: smallGroup.data.eventID}).eq('id', smallGroupSession.id).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateSmallGroupSession", error, smallGroupSession}, `Something went wrong updating the Small Group Session`)
        throw Error
    }

    let attendanceDetails = [];
    if(attendees){
        const existingAttendance = await supabase.from('Attendance').select('attendeeID').eq('eventInstanceID', smallGroupSession.id);
        const {itemsToAdd, itemsToDelete} = findAssociativeChanges(attendees.map(a => a.id), existingAttendance.data?.map(a => a.attendeeID));

        if(itemsToAdd && itemsToAdd.length > 0){
            const { error: errorAttendance } = await supabase.from('Attendance').insert(itemsToAdd?.map(a => ({eventInstanceID: smallGroupSession.id, attendeeID: a, organizationID: smallGroupSession.organizationID})));

            if(errorAttendance){
                ErrorHandler(Error(errorAttendance.message), {mutation: "updateSmallGroupSession_Attendance", errorAttendance})
            }
        }

        if(itemsToDelete && itemsToDelete.length > 0){
            const { error: errorAttendanceDelete } = await supabase.from('Attendance').delete().eq('eventInstanceID', smallGroupSession.id).in('attendeeID', itemsToDelete);

            if(errorAttendanceDelete){
                ErrorHandler(Error(errorAttendanceDelete.message), {mutation: "updateSmallGroupSession_AttendanceDelete", errorAttendanceDelete})
            }
        }

        if(itemsToAdd?.length) attendanceDetails.push(`- Added ${itemsToAdd.length} attendee(s)`);
        if(itemsToDelete?.length) attendanceDetails.push(`- Removed ${itemsToDelete.length} attendee(s)`);
    }

    const groupName = smallGroup.data?.name || 'Small Group';
    insertActivity({ subjectType: 'SmallGroupSession', subjectID: smallGroupID, subjectName: groupName, verb: 'update', details: attendanceDetails.join('\n') || undefined }).catch(() => {});

    if(leaderID){
        const { error: errorSpeaker } = await supabase.from('EventSpeakers').upsert({
            eventInstanceID: smallGroupSession.id,
            personID: leaderID,
            organizationID: smallGroupSession.organizationID
        }, { onConflict: 'eventInstanceID,personID' });

        if(errorSpeaker){
            ErrorHandler(Error(errorSpeaker.message), {mutation: "updateSmallGroupSession_Speaker", errorSpeaker})
        }
    }

    return data;
}

const deleteSmallGroupSession = async (sessionID) => {
    const { data: existing } = await supabase.from('EventInstances').select('startTime, Event:Events(name, SmallGroup:SmallGroups(id, name))').eq('id', sessionID).maybeSingle();

    const { error } = await supabase.from('EventInstances').delete().eq('id', sessionID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteSmallGroupSession", error, sessionID}, `Something went wrong deleting the Small Group Session`)
        throw Error
    }

    Toast.delayShow({type: 'success', text2: 'Successfully deleted Small Group Session'})

    const groupName = existing?.Event?.SmallGroup?.name || existing?.Event?.name || 'Small Group Session';
    const smallGroupID = existing?.Event?.SmallGroup?.id;
    insertActivity({ subjectType: 'SmallGroupSession', subjectID: smallGroupID, subjectName: groupName, verb: 'delete' }).catch(() => {});
}


// Reporting date ranges are org-local calendar days; inputs (Date,
// luxon DateTime, ISO string, 'yyyy-MM-dd') become org-timezone day
// boundaries for the timestamptz RPC params.
const _reportRangeBounds = async (startDate, endDate) => {
    const { start, end } = await reportRangeBounds(startDate, endDate);
    return { p_start_date: start, p_end_date: end };
};

const getSmallGroupMetrics = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_small_group_metrics', {
      p_small_group_type_id: smallGroupTypeID || null,
      ...(await _reportRangeBounds(startDate, endDate)),
    });
  
    if (error) throw error;
    return data;
};

const getSmallGroupTrends = async ({ smallGroupTypeID, startDate, endDate }) => {
  const { data, error } = await supabase.rpc('get_small_group_trends', {
    p_small_group_type_id: smallGroupTypeID || null,
    ...(await _reportRangeBounds(startDate, endDate)),
  });

  if (error) throw error;
  return data;
};

const getSmallGroupReportingStats = async (smallGroupTypeID) => {
    const { data, error } = await supabase.rpc('analytics_get_small_group_reporting_stats', {
        p_small_group_type_id: smallGroupTypeID
    });

    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupReportingStats", error}, `Something went wrong fetching the reporting stats`);
        return null;
    }

    return data?.[0] || null;
};

const getSmallGroupDateStats = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_small_group_date_stats', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });

    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupDateStats", error}, `Something went wrong fetching date-based stats`);
        return null;
    }

    return data?.[0] || null;
};

const getSmallGroupForMerge = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('SmallGroups').select(`
        id, name, status, eventID, leaderID,
        Leader:People!SmallGroups_leaderID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        SmallGroupType:SmallGroupTypes(id, name),
        Event:Events(EventInstances(id))
    `)
    .eq('organizationID', orgID)
    .eq('id', id)
    .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupForMerge", input: {id}}, `Something went wrong fetching Small Group for merge`);
        return null;
    }

    return data;
}

const mergeSmallGroups = async ({ sourceId, targetId }) => {
    const sourceGroup = await supabase.from('SmallGroups').select('eventID').eq('id', sourceId).maybeSingle();
    const targetGroup = await supabase.from('SmallGroups').select('eventID').eq('id', targetId).maybeSingle();

    if (sourceGroup.error || targetGroup.error || !sourceGroup.data?.eventID || !targetGroup.data?.eventID) {
        ErrorHandler(Error(sourceGroup.error?.message || targetGroup.error?.message || "Missing eventID"), {mutation: "mergeSmallGroups"}, `Something went wrong merging Small Groups`);
        throw Error();
    }

    // Move all sessions (EventInstances) from source's event to target's event
    const { error: moveError } = await supabase
        .from('EventInstances')
        .update({ eventID: targetGroup.data.eventID })
        .eq('eventID', sourceGroup.data.eventID);

    if (moveError) {
        ErrorHandler(Error(moveError.message), {mutation: "mergeSmallGroups-moveSessions", moveError}, `Something went wrong moving sessions during merge`);
        throw Error();
    }

    // Delete the source small group
    const { error: deleteError } = await supabase.from('SmallGroups').delete().eq('id', sourceId);

    if (deleteError) {
        ErrorHandler(Error(deleteError.message), {mutation: "mergeSmallGroups-delete", deleteError}, `Sessions were moved, but something went wrong deleting the duplicate Small Group`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Small Groups merged successfully!' });
    return { sourceId, targetId };
}

const getSmallGroupTeachersDetail = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_small_group_teachers_detail', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupTeachersDetail", error}, `Something went wrong fetching leaders detail`);
        return [];
    }
    return data || [];
};

const getSmallGroupStudentsDetail = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_small_group_students_detail', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupStudentsDetail", error}, `Something went wrong fetching members' detail`);
        return [];
    }
    return data || [];
};

const getActiveGroupsDetail = async ({ smallGroupTypeID }) => {
    const { data, error } = await supabase.rpc('get_active_groups_detail', {
        p_small_group_type_id: smallGroupTypeID
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getActiveGroupsDetail", error}, `Something went wrong fetching active groups detail`);
        return [];
    }
    return data || [];
};

const getActiveLeadersDetail = async ({ smallGroupTypeID }) => {
    const { data, error } = await supabase.rpc('get_active_leaders_detail', {
        p_small_group_type_id: smallGroupTypeID
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getActiveLeadersDetail", error}, `Something went wrong fetching active leaders detail`);
        return [];
    }
    return data || [];
};

const getActiveStudentsDetail = async ({ smallGroupTypeID }) => {
    const { data, error } = await supabase.rpc('get_active_students_detail', {
        p_small_group_type_id: smallGroupTypeID
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getActiveStudentsDetail", error}, `Something went wrong fetching active members' detail`);
        return [];
    }
    return data || [];
};

const getSessionsDetail = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_sessions_detail', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getSessionsDetail", error}, `Something went wrong fetching sessions detail`);
        return [];
    }
    return data || [];
};

const getNewGroupsDetail = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_new_groups_detail', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getNewGroupsDetail", error}, `Something went wrong fetching new groups detail`);
        return [];
    }
    return data || [];
};

const getCompletedGroupsDetail = async ({ smallGroupTypeID, startDate, endDate }) => {
    const { data, error } = await supabase.rpc('get_completed_groups_detail', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate))
    });
    if (error) {
        ErrorHandler(Error(error.message), {query: "getCompletedGroupsDetail", error}, `Something went wrong fetching completed groups detail`);
        return [];
    }
    return data || [];
};

const getSmallGroupLeaderReport = async ({ smallGroupTypeID, startDate, endDate, includeWithoutMeetings = false }) => {
    const { data, error } = await supabase.rpc('get_small_group_leader_report', {
        p_small_group_type_id: smallGroupTypeID,
        ...(await _reportRangeBounds(startDate, endDate)),
        p_include_without_meetings: includeWithoutMeetings,
    });

    if (error) {
        const reportError = Error(error.message);
        ErrorHandler(reportError, { query: "getSmallGroupLeaderReport", error }, `Something went wrong fetching the leader report`);
        throw reportError;
    }

    return data || [];
};

const getSmallGroupSessionsPaginated = async ({ smallGroupTypeID, startDate, endDate, smallGroupID, teacherID, searchTerm, sortKey = null, sortDirection = null, pageParam = 0 }) => {
    const pageLimit = 50;
    const { data, error } = await supabase.rpc('get_small_group_sessions_paginated', {
        p_small_group_type_id: smallGroupTypeID || null,
        ...(await _reportRangeBounds(startDate, endDate)),
        p_small_group_id: smallGroupID || null,
        p_teacher_id: teacherID || null,
        p_search_term: searchTerm || null,
        p_page_offset: pageParam * pageLimit,
        p_page_limit: pageLimit,
        p_sort_key: sortKey || null,
        p_sort_direction: sortDirection || null
    });

    if (error) {
        ErrorHandler(Error(error.message), {query: "getSmallGroupSessionsPaginated", error}, `Something went wrong fetching sessions`);
        return { data: [], nextPage: undefined };
    }

    return { 
        data: data || [], 
        nextPage: data?.length === pageLimit ? pageParam + 1 : undefined 
    };
};

export {
    getSmallGroupTypes,
    getSmallGroupType,
    saveSmallGroupType,
    deleteSmallGroupType,
    getSmallGroups,
    getSmallGroupsForDropdown,
    getSmallGroup,
    saveSmallGroup,
    deleteSmallGroup,
    saveSmallGroupSession,
    deleteSmallGroupSession,
    getSmallGroupMetrics,
    getSmallGroupTrends,
    getSmallGroupSession,
    getSmallGroupReportingStats,
    getSmallGroupDateStats,
    getSmallGroupTeachersDetail,
    getSmallGroupStudentsDetail,
    getActiveGroupsDetail,
    getActiveLeadersDetail,
    getActiveStudentsDetail,
    getSessionsDetail,
    getNewGroupsDetail,
    getCompletedGroupsDetail,
    getSmallGroupLeaderReport,
    getSmallGroupSessionsPaginated,
    getSmallGroupForMerge,
    mergeSmallGroups,
}